import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MSTContext } from "../../../stores/Root";
import { IProjectModel } from "../../../stores/project/projectModel";
import { dateFormatter } from "../../../helpers/timeAgo.helpers";
import "./ProjectGalleryCard.scss";
import { Image, Dropdown, Menu, Modal, Input, Button, message } from "antd";
import {
  KebabMenu,
  EditIconDynamicData,
  DuplicateIcon,
  RenameIcon,
} from "../../../assets/Icons";
import { PATHS, toPath } from "../../../PATHS";
import { UpdateProjectInput } from "../../../API";

const fallback =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAOCAYAAAAvxDzwAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAD0SURBVHgB3ZQ9DgFBFIDfGzQqLRHZ1k8kG/TcgBtwApxAnAAnwAm4ARcQGrRbSChVCsm+5+3GLsXushuVr5j/fPNm8mYQhO3h0iWgHjBrEIwBwMNKMTvzW4Cb/akt1RTCgNivFDJjr6k4gBrIrhAK5q6UtnB7PGtMtBBDSiXvugg/HtML7Smrk0kLQEhZfbolmgoiYt870cqROUQWEpuedxhZ6McfC9k0W5Jt158Jq+XcUsWULkluuDJUVxG+Br4GrScIoOfThkJsMPMcEdci38WJaSKDozA+eSlDp2lJpWq7UdZKWckn7ryH7h8ZGsTcD/ocHsyrU6deRoztAAAAAElFTkSuQmCC";

export default function ProjectGalleryCard({
  project,
  handleClick,
  userHasMultipleAccounts,
}: {
  project: IProjectModel;
  handleClick: (id: string) => void;
  userHasMultipleAccounts: boolean;
}) {
  const {
    projectsStore: { saveProject, loadProjectBasicInfo, duplicateProject },
  } = useContext(MSTContext);

  const navigate = useNavigate();

  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState(project.title);
  const [isRenaming, setIsRenaming] = useState(false);

  const [duplicationModalOpen, setDuplicationModalOpen] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [duplicationError, setDuplicationError] = useState<string | null>(null);

  const showRenameModal = () => setRenameModalOpen(true);
  const closeRenameModal = () => {
    if (!isRenaming) setRenameModalOpen(false);
  };

  const showDuplicationModal = () => {
    setDuplicationModalOpen(true);
    setDuplicationError(null); // Reset error on new open
  };

  const closeDuplicationModal = () => {
    setDuplicationModalOpen(false);
    setIsDuplicating(false);
    setDuplicationError(null);
  };

  const confirmRename = async (upi: UpdateProjectInput) => {
    setIsRenaming(true);
    try {
      await saveProject({ ...upi, id: project.id });
      await loadProjectBasicInfo(project.id);
      closeRenameModal();
      message.success("Project renamed successfully.");
    } catch (err) {
      console.error(err);
      message.error("Error renaming project.");
    } finally {
      setIsRenaming(false);
    }
  };

  const date = `${
    project?.workSpaceUpdatedAt || project.publishedAt || project?.updatedAt
      ? dateFormatter(
          new Date(
            project?.workSpaceUpdatedAt ||
              project.publishedAt ||
              (project?.updatedAt as any)
          )
        )
      : ""
  }`;

  const navigateToPath = (projectId: string) => {
    navigate(toPath(PATHS.project, projectId));
  };

  const handleMenuClick: Parameters<typeof Menu>[0]["onClick"] = async (
    info
  ) => {
    info.domEvent.stopPropagation();
    switch (info.key) {
      case "edit":
        navigateToPath(project.id);
        break;
      case "duplicate":
        showDuplicationModal();
        setIsDuplicating(true);
        try {
          const newProjectId = await duplicateProject(project.id);
          if (newProjectId) {
            await loadProjectBasicInfo(newProjectId); // Call with the new ID
            message.success("Project duplicated successfully.");
            closeDuplicationModal();
          } else {
            // This else block might be redundant if duplicateProject always throws on API failure
            // or returns undefined only in specific, non-error scenarios not yet handled.
            // However, it's good for robustness.
            console.error(
              "Duplication succeeded but no new project ID was returned."
            );
            setDuplicationError(
              "Failed to finalize project duplication. Please try again."
            );
            setIsDuplicating(false);
          }
        } catch (err) {
          console.error("Error duplicating project:", err);
          // Check if err is an instance of Error to safely access message property
          const errorMessage = err instanceof Error ? err.message : "An unknown error occurred.";
          setDuplicationError(
            `Failed to duplicate project: ${errorMessage}. Please try again.`
          );
          setIsDuplicating(false); // Stop loading to show error and button
        }
        break;
      case "rename":
        showRenameModal();
        break;
    }
  };

  const menu = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="edit">
        <div className="dropdown-items">
          <EditIconDynamicData /> <span>Edit</span>
        </div>
      </Menu.Item>
      <Menu.Item key="duplicate">
        <div className="dropdown-items">
          <DuplicateIcon /> <span>Duplicate</span>
        </div>
      </Menu.Item>
      <Menu.Item key="rename">
        <div className="dropdown-items">
          <RenameIcon /> <span>Rename</span>
        </div>
      </Menu.Item>
    </Menu>
  );

  const isRenameDisabled = newProjectName.trim().length < 1 || isRenaming;

  return (
    <>
      <div
        className="project-gallery-card"
        onClick={() => handleClick(project.id)}
      >
        {userHasMultipleAccounts && (
          <div
            className="project-account-name text"
            title={project.projectAccountId}
          >
            {project.projectAccountId}
          </div>
        )}

        <div className="video-thumb-container">
          <Image
            className="video-thumb"
            src={project.thumbS3Url || "error"}
            alt="project thumbnail"
            fallback={fallback}
            preview={false}
            placeholder
          />
          <div className="background-effect" />
        </div>

        <div className="project-details">
          <div className="project-gallery-card-title" title={project.title}>
            {project.title}
          </div>

          <div className="project-gallery-card-date">
            <span>{date}</span>
            <Dropdown overlay={menu} trigger={["click"]} placement="bottomLeft">
              <div
                className="project-gallery-card-date-icon"
                onClick={(e) => e.stopPropagation()}
              >
                <KebabMenu />
              </div>
            </Dropdown>
          </div>
        </div>
      </div>

      <Modal
        open={renameModalOpen}
        onCancel={closeRenameModal}
        footer={null}
        centered
        width={400}
        title="Rename Project"
        destroyOnHidden
      >
        <Input
          value={newProjectName}
          onChange={(e) => setNewProjectName(e.target.value)}
          placeholder="New project name"
          style={{ marginBottom: 16 }}
          autoFocus
          disabled={isRenaming}
          maxLength={60}
        />

        <div className="modal-footer">
          <Button
            className="cancel-button"
            onClick={closeRenameModal}
            disabled={isRenaming}
          >
            Cancel
          </Button>

          <Button
            type="primary"
            loading={isRenaming}
            disabled={isRenameDisabled}
            onClick={() =>
              confirmRename({ id: project.id, title: newProjectName.trim() })
            }
          >
            {isRenaming ? "Renaming..." : "Rename"}
          </Button>
        </div>
      </Modal>

      <Modal
        open={duplicationModalOpen}
        onCancel={!isDuplicating ? closeDuplicationModal : undefined} // Prevent closing while actively duplicating
        footer={null} // We'll manage footer content based on state
        centered
        width={400}
        title="Duplicate Project"
        closable={!isDuplicating} // Allow closing if not duplicating or if there's an error
        maskClosable={!isDuplicating && !duplicationError}
      >
        {isDuplicating && !duplicationError && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            {/* Consider adding an Ant Design Spinner here for better UX */}
            <p>Duplicating project, please wait...</p>
          </div>
        )}
        {duplicationError && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <p style={{ color: "red", marginBottom: "16px" }}>
              {duplicationError}
            </p>
            <Button onClick={closeDuplicationModal}>Close</Button>
          </div>
        )}
        {/* Optional: Success message within the modal if you don't close it immediately */}
        {/* {!isDuplicating && !duplicationError && duplicationModalOpen && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <p>Project duplicated successfully!</p>
            <Button onClick={closeDuplicationModal} style={{ marginTop: "10px" }}>
              Close
            </Button>
          </div>
        )} */}
      </Modal>
    </>
  );
}
